import React, { useEffect, useMemo, useState } from 'react';
import {
  BooleanInput,
  Create,
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  FileInput,
  Filter,
  NumberInput,
  SelectInput,
  TextField,
  TextInput,
  ReferenceInput,
  useRecordContext,
  ChipField,
  useResourceContext,
  useDataProvider,
} from 'react-admin';
import AudioFileField from '../components/AudioFileField';
import HNForm from '../components/HNForm';
import HNList from '../components/HNList';
import { useNavigate } from 'react-router-dom';
import { useFormContext, useWatch } from 'react-hook-form';

const TrackFilter: React.FC<any> = (props) => {
  const { filterValues } = props;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedTitleId, setSelectedTitleId] = useState<string | null>(() => localStorage.getItem('selectedTitleId'));

  useEffect(() => {
    if (filterValues?.title_id) {
      setSelectedTitleId(filterValues.title_id);
      localStorage.setItem('selectedTitleId', filterValues.title_id);
    }
  }, [filterValues?.title_id]);

  return (
    <Filter {...props}>
      <ReferenceInput
        perPage={10000}
        source="title_id"
        reference="title"
        sort={{ field: 'position', order: 'ASC' }}
        alwaysOn>
        <SelectInput optionText="name" resettable />
      </ReferenceInput>
    </Filter>
  );
};

const TrackTitle: React.FC = () => {
  const record = useRecordContext<any>();
  return <span>{record && record.name ? `Track "${record.name}"` : 'Create Track'}</span>;
};

const TrackFormInner: React.FC<any> = (props) => {
  const dataProvider = useDataProvider();
  const { setValue, control, getValues } = useFormContext();
  const [baseTrackId, setBaseTrackId] = useState<string | null>(null);
  const [baseTrack, setBaseTrack] = useState<any>(null);
  const initialBaseTrackId = useMemo(() => getValues().base_track_id || null, []);
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  const record = getValues();

  const isUpdate = props.mode === 'update';

  const isBase = !!baseTrackId;
  const isDisabled = (field?: string) => {
    const enabledOnCreateFields = ['base_track_id', 'name', 'position', 'title', 'base_track_title_id'];
    if (isBase) {
      if (!field) return true;
      return !enabledOnCreateFields.includes(field);
    }
    return false;
  };

  // Watch base_track_id field
  const watchedBaseTrackId = useWatch({ control, name: 'base_track_id' });

  useEffect(() => {
    if (watchedBaseTrackId !== baseTrackId) {
      setBaseTrackId(watchedBaseTrackId);
    }
  }, [watchedBaseTrackId, baseTrackId]);

  useEffect(() => {
    if (baseTrackId) {
      dataProvider.getOne('track', { id: baseTrackId }).then((res: any) => {
        const shouldSkipOverride = isUpdate && isFirstFetch && !!initialBaseTrackId;
        setBaseTrack(res.data);
        if (!shouldSkipOverride) {
          setValue('name', res.data.name);
          setValue('position', res.data.position);
          setValue('positives', res.data.positives ?? []);
          setValue('sound_muted', res.data.sound_muted);
          setValue('silence_start', res.data.silence_start);
          setValue('silence_end', res.data.silence_end);
          setValue('file', res.data.file);
          setValue('file_id', res.data.file_id);
        }
        setIsFirstFetch(false);
      });
    } else {
      setBaseTrack(null);
    }
  }, [baseTrackId, dataProvider, setValue, isUpdate]);

  useEffect(() => {
    if (isUpdate && record.base_track_id && !record.title_id) {
      dataProvider.getOne('track', { id: record.base_track_id }).then((res: any) => {
        setValue('title_id', res.data.title_id);
      });
    }
  }, [isUpdate, record.base_track_id, record.title_id, dataProvider, setValue]);

  return (
    <>
      <ReferenceInput
        source="title_id"
        label="Title"
        reference="title"
        perPage={10000}
        sort={{ field: 'position', order: 'ASC' }}>
        <SelectInput optionText="name" readOnly={isDisabled('title')} />
      </ReferenceInput>
      <ReferenceInput
        source="base_track_id"
        label="Base Track (optional)"
        reference="track"
        perPage={10000}
        sort={{ field: 'title.position', order: 'ASC' }}
        filter={{
          base_track_id: {
            format: 'hasura-raw-query',
            value: { _is_null: true },
          },
        }}
        allowEmpty>
        <SelectInput optionText="name" resettable readOnly={isDisabled('base_track_id')} />
      </ReferenceInput>
      {isBase && baseTrack ? (
        <div style={{ margin: '8px 0' }}>
          Base track: <a href={`#/track/${baseTrack.id}`}>{baseTrack.name}</a>
        </div>
      ) : null}
      <TextInput source="name" validate={props.validateName} readOnly={isDisabled('name')} />
      <FileInput
        source="file"
        multiple={false}
        accept="audio/*"
        validate={isDisabled() ? undefined : props.validateFile}
        readOnly={isDisabled()}
        sx={{
          '& .RaFileInputPreview-removeButton': {
            display: isDisabled() ? 'none' : 'block',
          },
        }}>
        <AudioFileField />
      </FileInput>
      <NumberInput
        source="position"
        label="Position"
        validate={props.validatePosition}
        readOnly={isDisabled('position')}
      />
      <TextInput
        source="positives"
        multiline
        label="Positive statements"
        parse={(positives?: string) => positives?.split('\n')}
        format={(positives?: string[]) => positives?.join('\n')}
        readOnly={isDisabled()}
      />
      <BooleanInput source="sound_muted" readOnly={isDisabled()} />
      <NumberInput source="silence_start" label="Fade in duration" readOnly={isDisabled()} />
      <NumberInput source="silence_end" label="Fade out duration" readOnly={isDisabled()} />
    </>
  );
};

const TrackForm: React.FC<any> = (props) => (
  <HNForm>
    <TrackFormInner {...props} />
  </HNForm>
);
const rowSx = (record: any, index: number) => (record.base_track_id ? { backgroundColor: '#643DB326' } : {});
export const TrackList: React.FC<any> = (props) => (
  <HNList {...props} filters={<TrackFilter />} sort={{ field: 'position', order: 'ASC' }}>
    <Datagrid rowSx={rowSx}>
      <TextField source="name" />
      <TextField source="position" />
      <ChipField source="title.name" label="Title" />
      <EditButton />
      <DeleteButton />
    </Datagrid>
  </HNList>
);

export const TrackCreate: React.FC<any> = (props) => {
  const [selectedTitleId] = useState<string | null>(() => localStorage.getItem('selectedTitleId'));
  const navigate = useNavigate();
  const resource = useResourceContext();

  return (
    <Create
      title={<TrackTitle />}
      {...props}
      record={{ title_id: selectedTitleId }}
      transform={(data: any) => {
        if (data.base_track_id) {
          return {
            ...data,
            file: undefined,
          };
        }
        return data;
      }}
      mutationOptions={{
        onSuccess: () => {
          navigate(`/${resource}`);
        },
      }}>
      <TrackForm mode="create" />
    </Create>
  );
};

export const TrackEdit: React.FC<any> = (props) => (
  <Edit title={<TrackTitle />} {...props}>
    <TrackForm mode="update" />
  </Edit>
);
